if (!customElements.get('td-cart-drawer-footer')) {
  class TDCartDrawerFooter extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.handleGiftChange = this.handleGiftChange.bind(this);
      this.handlePromoSubmit = this.handlePromoSubmit.bind(this);
      this.handlePromoClick = this.handlePromoClick.bind(this);
    }

    connectedCallback() {
      this.cartDrawer = this.closest('cart-drawer');
      this.giftCheckbox = this.querySelector('[data-td-cart-footer-gift-checkbox]');
      this.promoForm = this.querySelector('[data-td-cart-footer-promo-form]');
      this.promoInput = this.querySelector('[data-td-cart-footer-promo-input]');
      this.promoFeedback = this.querySelector('[data-td-cart-footer-promo-feedback]');
      this.discountList = this.querySelector('[data-td-cart-footer-discount-list]');

      if (this.giftCheckbox) {
        this.giftCheckbox.addEventListener('change', this.handleGiftChange);
      }

      if (this.promoForm) {
        this.promoForm.addEventListener('submit', this.handlePromoSubmit);
      }

      this.addEventListener('click', this.handlePromoClick);
      this.syncPromoVisibility();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      if (this.giftCheckbox) {
        this.giftCheckbox.removeEventListener('change', this.handleGiftChange);
      }

      if (this.promoForm) {
        this.promoForm.removeEventListener('submit', this.handlePromoSubmit);
      }

      this.removeEventListener('click', this.handlePromoClick);
    }

    createAbortController() {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      return this.abortController;
    }

    setBusy(isBusy) {
      this.toggleAttribute('aria-busy', isBusy);

      this.querySelectorAll('button, input').forEach((element) => {
        if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
          element.disabled = isBusy;
        }
      });
    }

    setPromoMessage(message, type = 'status') {
      if (!this.promoFeedback) return;

      this.promoFeedback.textContent = message;
      this.promoFeedback.hidden = !message;
      this.promoFeedback.classList.toggle('td-cart-drawer-footer__promo-feedback--error', type === 'error');
      this.promoFeedback.classList.toggle('td-cart-drawer-footer__promo-feedback--status', type === 'status');
    }

    clearPromoMessage() {
      this.setPromoMessage('', 'status');
    }

    getMessage(name) {
      return this.dataset[name] || '';
    }

    formatMessage(template, code) {
      return template.replace('[code]', code);
    }

    formatTemplate(template, replacements) {
      return Object.entries(replacements).reduce(
        (result, [key, value]) => result.replaceAll(`[${key}]`, value),
        template
      );
    }

    announce(message) {
      const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
      if (!liveRegion) return;

      liveRegion.textContent = message;
      liveRegion.setAttribute('aria-hidden', 'false');

      window.setTimeout(() => {
        liveRegion.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    getSectionIds() {
      if (this.cartDrawer && typeof this.cartDrawer.getSectionsToRender === 'function') {
        return this.cartDrawer.getSectionsToRender().map((section) => section.id);
      }

      return ['cart-drawer', 'cart-icon-bubble'];
    }

    getAppliedDiscountCodes() {
      return this.getDiscountEntriesFromDom().map((entry) => entry.code);
    }

    getDiscountApplicationsFromResponse(parsedState) {
      if (!Array.isArray(parsedState?.cart_level_discount_applications)) return [];

      return parsedState.cart_level_discount_applications;
    }

    matchDiscountApplication(code, applications) {
      const normalizedCode = code.toUpperCase();

      return applications.find((application) => {
        const title = typeof application?.title === 'string' ? application.title.trim() : '';
        const normalizedTitle = title.toUpperCase();

        return normalizedTitle === normalizedCode || normalizedTitle.includes(normalizedCode);
      });
    }

    buildDiscountEntry(code, application) {
      return {
        code,
        valueType: application?.value_type || application?.valueType || '',
        value: application?.value ?? '',
        allocatedAmount: application?.total_allocated_amount ?? application?.totalAllocatedAmount ?? '',
      };
    }

    formatPercentageValue(value) {
      const numberValue = Number(value);

      if (Number.isNaN(numberValue)) return String(value);

      const rounded = Math.round(numberValue * 100) / 100;

      return rounded % 1 === 0 ? String(Math.round(rounded)) : String(rounded);
    }

    getDiscountEntriesFromResponse(parsedState) {
      const applications = this.getDiscountApplicationsFromResponse(parsedState);

      if (Array.isArray(parsedState?.discount_codes) && parsedState.discount_codes.length) {
        return parsedState.discount_codes
          .filter(
            (discount) =>
              discount?.applicable !== false &&
              typeof discount.code === 'string' &&
              discount.code.trim() !== ''
          )
          .map((discount) => {
            const code = discount.code.trim();
            const application = this.matchDiscountApplication(code, applications);

            return this.buildDiscountEntry(code, application);
          });
      }

      return applications
        .filter((application) => typeof application.title === 'string' && application.title.trim() !== '')
        .map((application) => this.buildDiscountEntry(application.title.trim(), application));
    }

    getDiscountEntriesFromDom() {
      return Array.from(this.querySelectorAll('[data-td-cart-footer-discount-code]'))
        .map((element) => ({
          code: element.dataset.tdCartFooterDiscountCode || '',
          valueType: element.dataset.tdCartFooterDiscountValueType || '',
          value: element.dataset.tdCartFooterDiscountValue || '',
          allocatedAmount: element.dataset.tdCartFooterDiscountAllocated || '',
        }))
        .filter((entry) => entry.code);
    }

    formatMoney(cents, currency = 'USD') {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    }

    formatDiscountDescription(entry, currency = 'USD') {
      if (entry.valueType === 'percentage' && entry.value !== '') {
        return this.formatTemplate(this.getMessage('tdCartFooterDiscountChipPercentage'), {
          value: this.formatPercentageValue(entry.value),
        });
      }

      const hasAllocatedAmount = entry.allocatedAmount !== '' && entry.allocatedAmount != null;

      if ((entry.valueType === 'fixed_amount' || !entry.valueType) && hasAllocatedAmount) {
        const amount = this.formatMoney(Number(entry.allocatedAmount), currency);

        return this.formatTemplate(this.getMessage('tdCartFooterDiscountChipFixed'), {
          amount,
        });
      }

      return '';
    }

    formatDiscountChipLabel(entry, currency = 'USD') {
      const description = this.formatDiscountDescription(entry, currency);

      if (!description) return entry.code;

      return this.formatTemplate(this.getMessage('tdCartFooterDiscountChipLabel'), {
        code: entry.code,
        description,
        suffix: this.getMessage('tdCartFooterDiscountAppliedSuffix'),
      });
    }

    syncPromoVisibility() {
      const hasApplied =
        this.dataset.tdCartFooterHasAppliedCode === 'true' || this.getAppliedDiscountCodes().length > 0;

      if (hasApplied) {
        this.hidePromoForm();

        if (this.discountList instanceof HTMLElement) {
          this.discountList.hidden = false;
        }
      } else {
        this.showPromoForm();

        if (this.discountList instanceof HTMLElement) {
          this.discountList.hidden = true;
        }
      }
    }

    reconcilePromoState(discountEntries = [], currency = '') {
      if (discountEntries.length) {
        this.hidePromoForm();
        this.renderDiscountList(discountEntries, currency);
        this.dataset.tdCartFooterHasAppliedCode = 'true';
      } else {
        this.showPromoForm();
        this.clearDiscountList();
        this.dataset.tdCartFooterHasAppliedCode = 'false';
      }
    }

    hidePromoForm() {
      if (this.promoForm instanceof HTMLFormElement) {
        this.promoForm.hidden = true;
      }
    }

    showPromoForm() {
      if (this.promoForm instanceof HTMLFormElement) {
        this.promoForm.hidden = false;
      }
    }

    ensureDiscountList() {
      if (this.discountList instanceof HTMLElement) {
        return this.discountList;
      }

      const discountList = document.createElement('ul');
      discountList.className = 'td-cart-drawer-footer__discount-list';
      discountList.dataset.tdCartFooterDiscountList = '';
      discountList.setAttribute('role', 'list');
      discountList.setAttribute('aria-label', 'Discounts');

      this.promoFeedback?.insertAdjacentElement('afterend', discountList);
      this.discountList = discountList;

      return discountList;
    }

    clearDiscountList() {
      if (this.discountList instanceof HTMLElement) {
        this.discountList.innerHTML = '';
        this.discountList.hidden = true;
      }
    }

    escapeHTML(value) {
      return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }

    renderDiscountList(discountEntries, currency = '') {
      const discountList = this.ensureDiscountList();
      const removeLabelTemplate = this.getMessage('tdCartFooterRemoveLabel');
      const checkIconMarkup = '<svg aria-hidden="true" focusable="false" class="icon icon-checkmark" viewBox="0 0 24 24"><path clip-rule="evenodd" d="M20.6136 5.64877c.4199.36742.458 1.00751.0845 1.42204L10.1842 18.7406c-.37544.4167-1.02006.4432-1.42843.0588l-6.08403-5.7276c-.37942-.3572-.41574-.9524-.09021-1.3593.3592-.449 1.02811-.5108 1.4556-.1263l4.72039 4.2459c.41022.369 1.04179.336 1.41138-.0737l9.0435-10.02691c.3659-.40576.99-.44254 1.4012-.08272z" fill-rule="evenodd"/></svg>';

      discountList.innerHTML = discountEntries
        .map((entry) => {
          const escapedCode = this.escapeHTML(entry.code);
          const escapedLabel = this.escapeHTML(this.formatDiscountChipLabel(entry, currency));
          const metadataAttributes = [
            entry.valueType ? `data-td-cart-footer-discount-value-type="${this.escapeHTML(entry.valueType)}"` : '',
            entry.value !== '' ? `data-td-cart-footer-discount-value="${this.escapeHTML(String(entry.value))}"` : '',
            entry.allocatedAmount !== ''
              ? `data-td-cart-footer-discount-allocated="${this.escapeHTML(String(entry.allocatedAmount))}"`
              : '',
          ]
            .filter(Boolean)
            .join(' ');

          return `
            <li class="td-cart-drawer-footer__discount-chip" data-td-cart-footer-discount-code="${escapedCode}" ${metadataAttributes}>
              <span class="td-cart-drawer-footer__discount-chip-check" aria-hidden="true">${checkIconMarkup}</span>
              <span class="td-cart-drawer-footer__discount-chip-label">${escapedLabel}</span>
              <button
                type="button"
                class="td-cart-drawer-footer__discount-remove"
                data-td-cart-footer-remove-discount="${escapedCode}"
                aria-label="${this.escapeHTML(this.formatMessage(removeLabelTemplate, entry.code))}"
              >
                <span aria-hidden="true">&#215;</span>
              </button>
            </li>
          `;
        })
        .join('');

      discountList.hidden = false;
    }

    setAppliedDiscountEntries(discountEntries, currency = '') {
      const normalizedDiscountEntries = Array.isArray(discountEntries)
        ? discountEntries.filter((entry) => typeof entry?.code === 'string' && entry.code.trim() !== '')
        : [];

      this.reconcilePromoState(normalizedDiscountEntries, currency);
    }

    getCartUpdateUrl() {
      return window.routes?.cart_update_url || routes.cart_update_url;
    }

    async handleGiftChange(event) {
      if (!(event.currentTarget instanceof HTMLInputElement)) return;

      const nextValue = event.currentTarget.checked ? 'yes' : '';
      const previousChecked = !event.currentTarget.checked;
      const cartRoot = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${cartRoot}cart/update.js`, {
          ...fetchConfig(),
          body: JSON.stringify({
            attributes: { gift_order: nextValue },
          }),
        });

        if (!response.ok) {
          throw new Error('gift-update-failed');
        }
      } catch (error) {
        event.currentTarget.checked = previousChecked;
      }
    }

    async handlePromoSubmit(event) {
      event.preventDefault();

      if (!(this.promoInput instanceof HTMLInputElement)) return;

      const discountCode = this.promoInput.value.trim();
      if (!discountCode) return;

      const existingDiscounts = this.getAppliedDiscountCodes();
      const normalizedDiscountCode = discountCode.toUpperCase();

      if (existingDiscounts.some((code) => code.toUpperCase() === normalizedDiscountCode)) {
        this.promoInput.value = '';
        return;
      }

      this.clearPromoMessage();
      this.setBusy(true);

      const requestBody = JSON.stringify({
        discount: [...existingDiscounts, discountCode].join(','),
        sections: this.getSectionIds(),
        sections_url: window.location.pathname,
      });

      const abortController = this.createAbortController();

      try {
        const response = await fetch(this.getCartUpdateUrl(), {
          ...fetchConfig(),
          body: requestBody,
          signal: abortController.signal,
        });
        const parsedState = await response.json();

        if (!response.ok) {
          throw new Error('discount-apply-failed');
        }

        const applicableEntries = this.getDiscountEntriesFromResponse(parsedState);

        if (!applicableEntries.some((entry) => entry.code.toUpperCase() === normalizedDiscountCode)) {
          this.setPromoMessage(this.getMessage('tdCartFooterInvalidMessage'), 'error');
          this.announce(this.getMessage('tdCartFooterInvalidMessage'));
          this.promoInput.select();
          return;
        }

        this.renderDrawer(parsedState);

        const successMessage = this.formatMessage(this.getMessage('tdCartFooterAppliedMessage'), discountCode);
        this.announce(successMessage);
      } catch (error) {
        if (error.name !== 'AbortError') {
          const failureMessage = this.getMessage('tdCartFooterApplyFailedMessage');
          this.setPromoMessage(failureMessage, 'error');
          this.announce(failureMessage);
        }
      } finally {
        this.abortController = null;
        this.setBusy(false);
      }
    }

    async handlePromoClick(event) {
      if (!(event.target instanceof Element)) return;

      const removeButton = event.target.closest('[data-td-cart-footer-remove-discount]');

      if (!(removeButton instanceof HTMLButtonElement)) return;

      const discountCode = removeButton.dataset.tdCartFooterRemoveDiscount;
      if (!discountCode) return;

      const remainingDiscounts = this.getAppliedDiscountCodes().filter((code) => code !== discountCode);

      this.clearPromoMessage();
      this.setBusy(true);

      const abortController = this.createAbortController();

      try {
        const response = await fetch(this.getCartUpdateUrl(), {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: remainingDiscounts.join(','),
            sections: this.getSectionIds(),
            sections_url: window.location.pathname,
          }),
          signal: abortController.signal,
        });
        const parsedState = await response.json();

        if (!response.ok) {
          throw new Error('discount-remove-failed');
        }

        this.renderDrawer(parsedState);

        const successMessage = this.formatMessage(this.getMessage('tdCartFooterRemovedMessage'), discountCode);
        this.announce(successMessage);
      } catch (error) {
        if (error.name !== 'AbortError') {
          const failureMessage = this.getMessage('tdCartFooterApplyFailedMessage');
          this.setPromoMessage(failureMessage, 'error');
          this.announce(failureMessage);
        }
      } finally {
        this.abortController = null;
        this.setBusy(false);
      }
    }

    renderDrawer(parsedState) {
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsedState);

        const nextFooter = cartDrawer.querySelector('td-cart-drawer-footer');
        if (nextFooter instanceof TDCartDrawerFooter) {
          nextFooter.syncPromoVisibility();
        }
      }
    }
  }

  customElements.define('td-cart-drawer-footer', TDCartDrawerFooter);
}
